import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import client from '../api/client';

const AuthContext = createContext(null);
const USER_REVALIDATION_COOLDOWN_MS = 15000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastRevalidationAtRef = useRef(0);

  const syncCurrentUser = useCallback(async ({ clearAuthOnFailure = false } = {}) => {
    try {
      const response = await client.get('/api/users/profile');
      const userData = response.data?.user || response.data;
      setUser(userData);
      setIsAuthenticated(true);
      lastRevalidationAtRef.current = Date.now();
      return userData;
    } catch (error) {
      if (clearAuthOnFailure) {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setUser(null);
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      syncCurrentUser({ clearAuthOnFailure: true })
        .catch(() => {
          // handled in syncCurrentUser
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [syncCurrentUser]);

  const login = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
    lastRevalidationAtRef.current = Date.now();
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    lastRevalidationAtRef.current = 0;
  }, []);

  const reloadUser = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      return null;
    }

    try {
      return await syncCurrentUser();
    } catch {
      return null;
    }
  }, [syncCurrentUser]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const maybeRevalidateUser = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      const now = Date.now();
      if (now - lastRevalidationAtRef.current < USER_REVALIDATION_COOLDOWN_MS) {
        return;
      }

      void reloadUser();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        maybeRevalidateUser();
      }
    };

    window.addEventListener('focus', maybeRevalidateUser);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', maybeRevalidateUser);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, reloadUser]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    reloadUser,
  }), [user, isAuthenticated, loading, login, logout, reloadUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
